import { FieldSet } from "o4a-react";
import * as React from "react";
import {
  DefaultButton,
  FocusZone,
  FocusZoneTabbableElements,
  IButtonStyles,
  IconButton,
  mergeStyleSets,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext, SettingType } from "../../console/SettingsContext";
import { DetailsPanelId, InlineFormId, PageId } from "../../constants/pluginConstants";
import { footerHeight, MenuStyleMode } from "../../constants/uiConstants";
import { useAnalytics } from "../../hooks/useAnalytics";

const classNames = mergeStyleSets({
  actionFooter: {
    position: "fixed",
    padding: "10px 10px 0px 10px",
    bottom: `${footerHeight}px`,
    width: "100%",
    height: "44px",
  },
  menuBehaviorContainer: { display: "flex" },
  menuBehaviorLabel: {},
  menuBehaviorList: { display: "flex", listStyle: "none" },
  menuBehaviorListFlyoutItem: { marginRight: "15px" },
  buttons: {
    justifyContent: "flex-start",
    marginTop: 3,
  },
  itemTitle: {
    paddingTop: "5px",
    paddingBottom: "5px",
    fontSize: "14px",
    color: "#000000",
  },
});

const btnStyles: IButtonStyles = {
  root: {
    color: "white",
    width: "100%",
    height: "100%",
    borderRadius: 0,
    padding: "8px",
  },
  flexContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  icon: {
    height: "auto",
    lineHeight: "normal",
    margin: 0,
    paddingTop: "12px",
    paddingBottom: "2px",
  },
  rootHovered: {
    fontWeight: "600",
    borderColor: "transparent",
    boxShadow: "0 0 0 1px black",
    borderRadius: "4px",
  },
  rootChecked: {
    fontWeight: "600",
    borderColor: "transparent",
    boxShadow: "0 0 0 2px #0078d4",
    borderRadius: "4px",
  },
};

export enum ButtonTestIds {
  FlyoutButton = "flyout-button",
  DockedButton = "docked-button",
  ApplyAppearance = "apply-appearance-button",
  DiscardAppearance = "discard-appearance-button",
}

const actionBtnStyles: IButtonStyles = { root: { height: 24 } };

export const SettingAppearance = (): JSX.Element => {
  const { trackFormSave } = useAnalytics();

  const { menuMode, setSettingValue } = React.useContext<Settings>(SettingsContext);

  const [menuBehaviorStorage, setMenuBehaviorStorage] = React.useState<MenuStyleMode | undefined>(
    menuMode as MenuStyleMode,
  );
  const [menuBehavior, setMenuBehavior] = React.useState<MenuStyleMode>(
    menuMode as MenuStyleMode,
  );

  const [isButtonDisable, setButtonDisable] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (menuBehaviorStorage && menuBehavior && menuBehaviorStorage === menuBehavior) {
      setButtonDisable(true);
    } else {
      setButtonDisable(false);
    }
  }, [menuBehaviorStorage, menuBehavior]);

  const onClick = (): void => {
    setSettingValue(SettingType.MENU_MODE, menuBehavior);
    setMenuBehaviorStorage(menuBehavior);
  };

  const onCancel = (): void => {
    setMenuBehavior(menuBehaviorStorage || MenuStyleMode.FlyOut);
  };

  return (
    <>
      <FieldSet
        header={Messages.labels.appearance()}
      >
        <div className={classNames.menuBehaviorContainer}>
          <div className={classNames.menuBehaviorLabel}>Menu behavior</div>
          <ul className={classNames.menuBehaviorList}>
            <li className={classNames.menuBehaviorListFlyoutItem}>
              <IconButton
                iconProps={{ iconName: "flyout-menu-svg" }}
                styles={btnStyles}
                title={Messages.labels.flyout()}
                ariaLabel={Messages.labels.flyout()}
                onClick={() => {
                  setMenuBehavior(MenuStyleMode.FlyOut);
                }}
                checked={menuBehavior === MenuStyleMode.FlyOut}
                data-test-id={ButtonTestIds.FlyoutButton}
              >
                <div className={classNames.itemTitle} data-test-id="title">
                  {Messages.labels.flyout()}
                </div>
              </IconButton>
            </li>
            <li>
              <IconButton
                iconProps={{ iconName: "dock-menu-svg" }}
                styles={btnStyles}
                title={Messages.labels.docked()}
                ariaLabel={Messages.labels.docked()}
                onClick={() => {
                  setMenuBehavior(MenuStyleMode.Docking);
                }}
                checked={menuBehavior === MenuStyleMode.Docking}
                data-test-id={ButtonTestIds.DockedButton}
              >
                <div className={classNames.itemTitle} data-test-id="title">
                  {Messages.labels.docked()}
                </div>
              </IconButton>
            </li>
          </ul>
        </div>
      </FieldSet>
      <footer className={classNames.actionFooter}>
        <FocusZone handleTabKey={FocusZoneTabbableElements.all} isCircularNavigation>
          <Stack className={classNames.buttons} tokens={{ childrenGap: 8 }} horizontal>
            <PrimaryButton
              disabled={isButtonDisable}
              onClick={() => {
                trackFormSave(InlineFormId.SETTING_APPEARANCE, PageId.SETTINGS, DetailsPanelId.APPEARANCE);
                onClick();
              }}
              styles={actionBtnStyles}
              data-test-id={ButtonTestIds.ApplyAppearance}
            >
              {Messages.actions.apply()}
            </PrimaryButton>
            <DefaultButton
              styles={actionBtnStyles}
              disabled={isButtonDisable}
              onClick={() => {
                onCancel();
              }}
              data-test-id={ButtonTestIds.DiscardAppearance}
            >
              {Messages.actions.discardChanges()}
            </DefaultButton>
          </Stack>
        </FocusZone>
      </footer>
    </>
  );
};
